/* Local calendar dates, as strings.

   🔴 `toISOString()` IS NOT A DATE FORMATTER. It converts to UTC first, and Israel
   runs two or three hours ahead — so midnight local is 21:00 or 22:00 of the DAY
   BEFORE in UTC, and any date built from local midnight comes out one day early.

   This was not theoretical. The profit screen's default window is month-to-date,
   built as `new Date(y, m, 1)` and formatted with `toISOString().slice(0, 10)`.
   On 25/10/2026 it asked the server for **2026-09-30 → 2026-10-25**: every figure
   on the screen silently carried one extra day of the previous month, and had done
   since the screen was built. Ohad spotted it in the forecast panel's header
   (25/10/2026) — the screen itself never complained, because a window that is one
   day too wide still returns a perfectly plausible number.

   The same trap, narrower, hits `new Date().toISOString()` for "today" or "this
   month": correct most of the day, wrong between local midnight and 02:00 or 03:00,
   which is exactly when a nightly job or an early-morning reader would hit it.

   ⇒ format from the LOCAL components. Never through UTC. */

const pad = (n: number) => String(n).padStart(2, '0');

/** `2026-10-01` for the local calendar day `d` represents. */
export const localDay = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** `2026-10` for the local calendar month `d` falls in. */
export const localMonth = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

/** Today, as the person reading the screen would write it. */
export const today = (): string => localDay(new Date());

/** The month the reader is in. */
export const thisMonth = (): string => localMonth(new Date());

/** The month BEFORE the one the reader is in — `2026-09` through October.

    Built with `new Date(y, m - 1, 1)`, which rolls the year itself: in January
    `m - 1` is -1 and the Date constructor resolves that to December of the year
    before. Subtracting from the string, or from the day-of-month, would not —
    and `setMonth(-1)` on a 31st silently lands in a different month entirely. */
export const prevMonth = (): string => {
  const n = new Date();
  return localMonth(new Date(n.getFullYear(), n.getMonth() - 1, 1));
};
